import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import Header from './Header';
import Seo from './Seo';
import Footer from './Footer';
import FaqSchema from './FaqSchema';
import ProductSidebar from './ProductSidebar';
import { useUser } from '../contexts/UserContext';
import InterviewQuestionsPage from './InterviewQuestionsPage';
import './InterviewQuestionsLanding.css';

const HERO_IMG = '/images/interview-questions/hero-question-fountain.png';
const JD_IMG = '/images/interview-questions/jd-analyzer.png';
const TAILORED_IMG = '/images/interview-questions/tailored-answers.png';
const PRACTICE_IMG = '/images/interview-questions/practice-ready.png';

/* ---------- Three.js calm orbital canvas (clean + professional) ---------- */
const QuestionFountainCanvas = () => {
  const mountRef = useRef(null);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 80;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const sprite = (() => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.35, 'rgba(224,163,255,0.95)');
      g.addColorStop(0.7, 'rgba(147,112,219,0.4)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    })();

    // Three concentric orbital rings — slow, ambient, calm.
    const RINGS = [
      { count: 16, radius: 22, speed: 0.18, tilt: 0.0 },
      { count: 22, radius: 36, speed: -0.12, tilt: 0.5 },
      { count: 30, radius: 50, speed: 0.08, tilt: -0.4 },
    ];
    const ringObjs = RINGS.map((spec) => {
      const positions = new Float32Array(spec.count * 3);
      const angles = new Float32Array(spec.count);
      for (let i = 0; i < spec.count; i++) angles[i] = (i / spec.count) * Math.PI * 2;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        size: 1.8, map: sprite, transparent: true,
        depthWrite: false, blending: THREE.AdditiveBlending,
        color: 0xffffff, opacity: 0.85,
      });
      const pts = new THREE.Points(geo, mat);
      scene.add(pts);
      return { spec, geo, mat, angles, pts };
    });

    // Gentle ambient particle drift
    const fieldCount = 160;
    const fieldPos = new Float32Array(fieldCount * 3);
    const fieldSpeeds = new Float32Array(fieldCount);
    for (let i = 0; i < fieldCount; i++) {
      fieldPos[i * 3] = (Math.random() - 0.5) * 180;
      fieldPos[i * 3 + 1] = (Math.random() - 0.5) * 110;
      fieldPos[i * 3 + 2] = (Math.random() - 0.5) * 60;
      fieldSpeeds[i] = 0.03 + Math.random() * 0.08;
    }
    const fieldGeo = new THREE.BufferGeometry();
    fieldGeo.setAttribute('position', new THREE.BufferAttribute(fieldPos, 3));
    const fieldMat = new THREE.PointsMaterial({
      size: 0.9, map: sprite, transparent: true,
      depthWrite: false, blending: THREE.AdditiveBlending,
      color: 0xffffff, opacity: 0.35,
    });
    const field = new THREE.Points(fieldGeo, fieldMat);
    scene.add(field);

    // Soft central wireframe core
    const coreGeo = new THREE.IcosahedronGeometry(8, 1);
    const core = new THREE.Mesh(coreGeo, new THREE.MeshBasicMaterial({
      color: 0xe0a3ff, wireframe: true, transparent: true, opacity: 0.35,
    }));
    scene.add(core);

    let raf;
    let mx = 0, my = 0, t = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);
    const onResize = () => {
      width = mount.clientWidth; height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    const animate = () => {
      t += 0.01;
      ringObjs.forEach(({ spec, geo, angles }) => {
        const pos = geo.attributes.position.array;
        for (let i = 0; i < spec.count; i++) {
          const a = angles[i] + t * spec.speed;
          pos[i * 3]     = Math.cos(a) * spec.radius;
          pos[i * 3 + 1] = Math.sin(a) * spec.radius * Math.cos(spec.tilt);
          pos[i * 3 + 2] = Math.sin(a) * spec.radius * Math.sin(spec.tilt);
        }
        geo.attributes.position.needsUpdate = true;
      });

      const fp = fieldGeo.attributes.position.array;
      for (let i = 0; i < fieldCount; i++) {
        fp[i * 3 + 1] += fieldSpeeds[i] * 0.4;
        if (fp[i * 3 + 1] > 55) fp[i * 3 + 1] = -55;
      }
      fieldGeo.attributes.position.needsUpdate = true;

      core.rotation.x += 0.004;
      core.rotation.y += 0.005;

      camera.position.x += (mx * 6 - camera.position.x) * 0.04;
      camera.position.y += (-my * 4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      ringObjs.forEach(({ geo, mat }) => { geo.dispose(); mat.dispose(); });
      fieldGeo.dispose(); fieldMat.dispose();
      coreGeo.dispose(); core.material.dispose();
      sprite.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="iql-hero-canvas" aria-hidden="true" />;
};

/* ---------- 50-question grid demo (shows "50" filling in tiny by tiny) ---------- */
const Grid50Demo = () => {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting && revealed === 0) setRevealed(1); }),
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [revealed]);
  useEffect(() => {
    if (revealed === 0 || revealed >= 50) return;
    const t = setTimeout(() => setRevealed((r) => r + 1), 30);
    return () => clearTimeout(t);
  }, [revealed]);

  return (
    <section className="iql-section" ref={ref}>
      <div className="iql-section-grid iql-flip">
        <div className="iql-grid50-card">
          <div className="iql-grid50-head">
            <span className="iql-grid50-pulse" />
            <span>50 PERSONALIZED QUESTIONS</span>
            <span className="iql-grid50-count">{revealed}/50</span>
          </div>
          <div className="iql-grid50">
            {Array.from({ length: 50 }, (_, i) => (
              <div key={i} className={`iql-grid50-tile ${i < revealed ? 'lit' : ''}`} />
            ))}
          </div>
          <div className="iql-grid50-meta">
            <span>Behavioral · 18</span>
            <span>Technical · 22</span>
            <span>Resume-anchored · 10</span>
          </div>
        </div>

        <div className="iql-section-text">
          <span className="iql-eyebrow">VOLUME + RELEVANCE</span>
          <h2 className="iql-h2">
            Fifty questions, each <span className="iql-grad-text">individually tailored</span>.
          </h2>
          <p className="iql-lead">
            Not a generic question bank. Every one of the 50 is generated from your resume + the
            specific JD — across behavioral, technical, situational, and resume-anchored types.
          </p>
          <ul className="iql-bullet-list">
            <li><span className="iql-dot" />Pulls real company / role context from the JD</li>
            <li><span className="iql-dot" />Anchors questions to your actual roles & metrics</li>
            <li><span className="iql-dot" />Mix of question types — covers every angle</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

/* ---------- Tailored answer typewriter demo ---------- */
const QA_Q = 'Tell me about a time you had to choose between shipping fast and shipping right.';
const QA_A = 'At Acme last quarter we hit a deadline conflict — the launch demo was Tuesday, but our checkout still had a known race condition. I pushed to ship the demo behind a feature flag, then spent Wed-Thu fixing the race properly with the team. Result: demo landed on time, no incident in production, and we shipped clean two days later.';

const TailoredAnswerDemo = () => {
  const ref = useRef(null);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState('idle');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting && phase === 'idle') setPhase('typing'); }),
      { threshold: 0.45 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [phase]);

  useEffect(() => {
    if (phase !== 'typing') return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(QA_A.slice(0, i));
      if (i >= QA_A.length) { clearInterval(id); setPhase('done'); }
    }, 16);
    return () => clearInterval(id);
  }, [phase]);

  return (
    <section className="iql-section" ref={ref}>
      <div className="iql-section-grid">
        <div className="iql-section-text">
          <span className="iql-eyebrow">EVERY QUESTION HAS AN ANSWER</span>
          <h2 className="iql-h2">
            Each question comes paired with a <span className="iql-grad-text">tailored answer</span> from your story.
          </h2>
          <p className="iql-lead">
            No "figure it out yourself." For every question, the model produces a STAR-structured
            answer drawn from the actual roles, projects, and metrics in your resume — so you have
            a concrete starting point to rehearse and adapt.
          </p>
        </div>

        <div className="iql-qa-card">
          <div className="iql-qa-q">
            <span className="iql-qa-q-tag">QUESTION</span>
            <p>{QA_Q}</p>
          </div>
          <div className="iql-qa-a">
            <span className="iql-qa-a-tag">YOUR ANSWER · STAR-formatted</span>
            <p>
              {typed}
              {phase === 'typing' && <span className="iql-caret">|</span>}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Process flow ---------- */
const FLOW_STEPS = [
  { title: 'Drop the JD', body: 'Paste the full job description — or just the role title + company.' },
  { title: 'Add your resume', body: 'PDF / DOCX or "Use my profile" — the source of truth for every answer.' },
  { title: 'Generate', body: 'AI parses both, identifies key themes, and composes 50 questions.' },
  { title: 'Get tailored answers', body: 'Each question paired with a STAR-structured response from your real story.' },
  { title: 'Practice out loud', body: 'Read each answer aloud. Adapt. Internalize the structure, not the words.' },
  { title: 'Walk in ready', body: 'Whatever the interviewer opens with, you\'ve already practiced something like it.' },
];

const FlowSection = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(0); }),
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (active < 0 || active >= FLOW_STEPS.length - 1) return;
    const t = setTimeout(() => setActive((a) => a + 1), 700);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="iql-section" ref={ref}>
      <div className="iql-section-head">
        <span className="iql-eyebrow">HOW IT WORKS</span>
        <h2 className="iql-h2">
          Six steps. <span className="iql-grad-text">Fifty answered questions.</span>
        </h2>
        <p className="iql-lead iql-center">
          From "I have an interview Friday" to "I'm ready" in under five minutes.
        </p>
      </div>
      <div className="iql-flow-grid">
        {FLOW_STEPS.map((s, i) => (
          <div key={s.title} className={`iql-flow-node ${i <= active ? 'lit' : ''}`}>
            <div className="iql-flow-num">{String(i + 1).padStart(2, '0')}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < FLOW_STEPS.length - 1 && <div className={`iql-flow-link ${i < active ? 'lit' : ''}`} />}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- Stats ---------- */
const STATS = [
  { val: 50, suffix: '', label: 'Questions per session' },
  { val: 4, suffix: '', label: 'Question categories' },
  { val: 3, suffix: 'min', label: 'Avg generation time' },
  { val: 96, suffix: '%', label: 'Answers users keep' },
];
const useCounter = (target, start) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isFloat = !Number.isInteger(target);
    const dur = 1400;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = target * eased;
      setVal(isFloat ? Math.round(v * 10) / 10 : Math.round(v));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return val;
};
const StatTile = ({ stat, start }) => {
  const v = useCounter(stat.val, start);
  return (
    <div className="iql-stat">
      <div className="iql-stat-num">{v}{stat.suffix}</div>
      <div className="iql-stat-lbl">{stat.label}</div>
    </div>
  );
};
const StatsBand = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setStart(true); }),
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section className="iql-stats-band" ref={ref}>
      <div className="iql-stats-glow" />
      {STATS.map((s) => <StatTile key={s.label} stat={s} start={start} />)}
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQS = [
  { q: 'Why 50 questions and not 5?', a: 'Because real interviews jump topics. Recruiters mix behavioral with technical with situational with resume-anchored. Fifty gives you coverage so whatever they open with, you\'ve already rehearsed something close.' },
  { q: 'Are the answers generic?', a: 'No. Every answer is composed from your actual resume — your real roles, projects, and metrics. STAR-structured but in your voice, with your story.' },
  { q: 'Can I edit the questions or answers?', a: 'Yes. The output is yours to keep — copy, edit, rewrite, save. Use the answers as scaffolds and bring your own polish.' },
  { q: 'How do I get the most out of it?', a: 'Practice out loud. Reading silently builds familiarity but not delivery. Speak the answers, adapt them to your natural pace, then test yourself with the questions hidden.' },
  { q: 'Will the same JD give me the same questions?', a: 'Mostly different — there\'s deliberate variation in the generation. Run it again the day before your interview for a fresh practice pass.' },
  { q: 'Does this replace mock interviews?', a: 'It complements them. Use this to build a question library + rehearse answers. Use Mock Interview to simulate the live pressure.' },
];

const FaqAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="iql-section">
      <div className="iql-section-head">
        <span className="iql-eyebrow">QUESTIONS, ANSWERED</span>
        <h2 className="iql-h2">Everything you wanted to ask</h2>
      </div>
      <div className="iql-faq-list">
        {FAQS.map((f, i) => (
          <button key={f.q} type="button" className={`iql-faq-item ${open === i ? 'open' : ''}`} onClick={() => setOpen(open === i ? -1 : i)}>
            <div className="iql-faq-q">
              <span>{f.q}</span>
              <span className="iql-faq-chev">+</span>
            </div>
            <div className="iql-faq-a"><p>{f.a}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Main page ---------- */
const InterviewQuestionsLanding = () => {
  const navigate = useNavigate();
  const studioRef = useRef(null);
  // Session takeover state — the form + 50-question results panel are
  // gated behind an explicit "Open Generator" CTA so the landing page
  // stays marketing-only until the user opts in. Once opened the
  // session is sticky (no in-app close button by design); the user
  // exits via browser back, refresh, or by navigating to another route.
  const [generatorOpen, setGeneratorOpen] = useState(false);
  // Both CTAs (hero "Generate my 50 questions" AND the studio-section
  // "Generate Interview Questions") share the same scroll-defer logic.
  // Without it, clicking the bottom CTA when scrolled past the hero
  // leaves the page parked at that scroll position; the form (now at
  // y=0 of the document after the hero is hidden) renders above the
  // viewport and looks tucked behind the floating Terview header.
  // Double rAF gives React + the browser one paint each before we
  // scroll, so we read the NEW layout.
  const enterSession = useCallback(() => {
    setGeneratorOpen(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }, []);
  const openGenerator = enterSession;
  const goStudio = enterSession;
  const { user } = useUser();
  const setShowAuthModal = useCallback((show) => { if (show) navigate('/?openLogin=1'); }, [navigate]);

  // While the generator session is open, lock body scroll and apply
  // the marker class that hides marketing sections (see
  // InterviewQuestionsLanding.css `body.interview-questions-active`).
  useEffect(() => {
    if (!generatorOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('interview-questions-active');
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('interview-questions-active');
    };
  }, [generatorOpen]);

  const heroRef = useRef(null);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--mx', x);
      el.style.setProperty('--my', y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className={`iql-page ${generatorOpen ? 'iql-page--session' : ''}`}>
      <Seo
        title="Interview Questions & Answers Preparation"
        description="Prepare for role-specific interview questions with AI-generated answers tailored to your target job."
        path="/InterviewQuestions"
      />
      <Header setShowAuthModal={setShowAuthModal} />
      <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

      <section className="iql-hero" ref={heroRef}>
        <QuestionFountainCanvas />
        <div className="iql-hero-bg" />
        <img src={HERO_IMG} alt="" className="iql-hero-img" loading="eager" />

        <div className="iql-hero-content">
          <span className="iql-hero-pill">
            <span className="iql-pill-dot" /> 50 questions · STAR answers · 3 minutes
          </span>
          <h1 className="iql-h1">
            Fifty questions, <span className="iql-grad-text">fifty tailored answers</span>.
          </h1>
          <p className="iql-hero-sub">
            Drop your resume + the JD. Get back fifty role-specific interview questions and a
            STAR-structured answer for every single one — drawn from your real story. Practice
            until you can riff.
          </p>

          <div className="iql-hero-cta">
            <button className="iql-btn iql-btn-primary" onClick={goStudio}>
              <span>Generate my 50 questions</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="iql-btn iql-btn-ghost" onClick={() => navigate('/mockInterview')}>
              Run a mock instead
            </button>
          </div>

          <div className="iql-hero-checks">
            <span>✓ STAR-structured answers</span>
            <span>✓ Resume-aware</span>
            <span>✓ Generated in under 3 min</span>
          </div>
        </div>

        {/* Floating glass question card in hero */}
        <div className="iql-floater" aria-hidden="true">
          <div className="iql-floater-card">
            <div className="iql-floater-head">
              <span className="iql-floater-dot" />
              <span>Q · 23 OF 50</span>
            </div>
            <p className="iql-floater-q">"Walk me through how you'd debug a 3× latency regression in production."</p>
            <div className="iql-floater-tag">TECHNICAL · DEEP-DIVE</div>
            <div className="iql-floater-bar" />
            <div className="iql-floater-bar w70" />
            <div className="iql-floater-bar w85" />
          </div>
          <div className="iql-floater-glow" />
        </div>
      </section>

      <section className="iql-studio" ref={studioRef} id="studio">
        {generatorOpen ? (
          <div className="iql-session-shell">
            {/* Decorative ambient orbs that drift behind the form for a
                premium "this is a focused workspace" feel. Pointer-events
                disabled so they don't interfere with the form below. */}
            <div className="iql-session-aurora" aria-hidden="true">
              <span className="iql-aurora iql-aurora-1" />
              <span className="iql-aurora iql-aurora-2" />
              <span className="iql-aurora iql-aurora-3" />
            </div>


            <div className="iql-session-body">
              <InterviewQuestionsPage embedded />
            </div>
          </div>
        ) : (
          <div className="iql-studio-head">
            <span className="iql-eyebrow">QUESTIONS STUDIO</span>
            <h2 className="iql-h2">
              Generate them right here — <span className="iql-grad-text">in a focused session</span>.
            </h2>
            <p className="iql-lead iql-center">
              Drop the JD, link your resume, hit generate. Fifty questions and answers ready to copy
              in under three minutes.
            </p>
            <div className="iql-studio-cta">
              <button className="iql-btn iql-btn-primary" onClick={openGenerator}>
                <span>Generate Interview Questions</span>
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
              </button>
            </div>
          </div>
        )}
      </section>

      <Grid50Demo />
      <TailoredAnswerDemo />

      <section className="iql-section">
        <div className="iql-section-grid">
          <div className="iql-section-text">
            <span className="iql-eyebrow">JD-AWARE</span>
            <h2 className="iql-h2">
              We read the JD <span className="iql-grad-text">like a recruiter would</span>.
            </h2>
            <p className="iql-lead">
              The model parses the job description for must-haves, nice-to-haves, behavioral
              signals, and culture cues — then generates questions that probe each of them. No
              question is generic; every one ties to something on the JD.
            </p>
            <ul className="iql-bullet-list">
              <li><span className="iql-dot" />Skill / requirement coverage</li>
              <li><span className="iql-dot" />Culture-fit & behavioral signals</li>
              <li><span className="iql-dot" />Role-specific edge cases</li>
            </ul>
          </div>
          <div className="iql-img-frame">
            <img src={JD_IMG} alt="JD analyzer" />
            <div className="iql-img-frame-glow" />
          </div>
        </div>
      </section>

      <FlowSection />
      <StatsBand />

      <section className="iql-section">
        <div className="iql-section-grid iql-flip">
          <div className="iql-img-frame">
            <img src={TAILORED_IMG} alt="Tailored answers" />
            <div className="iql-img-frame-glow" />
          </div>
          <div className="iql-section-text">
            <span className="iql-eyebrow">RESUME-ANCHORED</span>
            <h2 className="iql-h2">
              Answers built from <span className="iql-grad-text">your real story</span> — never invented.
            </h2>
            <p className="iql-lead">
              Every answer pulls from your resume's actual roles and metrics. We never invent
              experience, never fabricate numbers, never claim something that isn't there. STAR
              structure + your story = answers you can defend in the room.
            </p>
          </div>
        </div>
      </section>

      <section className="iql-section">
        <div className="iql-section-grid">
          <div className="iql-section-text">
            <span className="iql-eyebrow">PRACTICE-FIRST</span>
            <h2 className="iql-h2">
              Built to <span className="iql-grad-text">rehearse</span>, not just read.
            </h2>
            <p className="iql-lead">
              Reading silently builds familiarity. Speaking out loud builds delivery. Use the
              questions to drill — answer hidden first, then reveal and compare. Every reps the
              shape into your muscle memory.
            </p>
          </div>
          <div className="iql-img-frame">
            <img src={PRACTICE_IMG} alt="Practice ready" />
            <div className="iql-img-frame-glow" />
          </div>
        </div>
      </section>

      <FaqAccordion />

      <section className="iql-cta-final">
        <div className="iql-cta-card">
          <div className="iql-cta-glow" />
          <h2 className="iql-h2">
            Walk in with <span className="iql-grad-text">fifty rehearsed answers</span>.
          </h2>
          <p>Stop guessing what they'll ask. Generate the bank, run the reps, win the room.</p>
          <div className="iql-hero-cta center">
            <button className="iql-btn iql-btn-primary" onClick={goStudio}>
              <span>Generate my 50 questions</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="iql-btn iql-btn-ghost" onClick={() => navigate('/mockInterview')}>Run a mock</button>
          </div>
        </div>
      </section>

      <FaqSchema faqs={FAQS} />
      <Footer />
    </div>
  );
};

export default InterviewQuestionsLanding;
